import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { queryClient } from '~/client/queryClient';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { RouteConfig } from '~/routes/routeConfig';
import { useF24OcrListQuery } from '~/services/f24';
import { useNotificationsToken } from '~/services/user';
import { SibillSocketChannel } from '~/sockets/sibill-socket';
import { useSibillSocket } from '~/state/notifications/socket';
import { F24QueryKeys } from '~/types/taxes';

interface F24OcrReadyPayload {
  ocr_job_id: string;
  company_id?: string;
  page_number?: number;
}

/**
 * The ocr_job_id from the backend has the shape `YYYYMMDDHHMMSS-uuid`.
 * The correlation id used as the socket topic is everything after the first `-`.
 */
const getCorrelationId = (ocrJobId: string): string => {
  const idx = ocrJobId.indexOf('-');
  return idx !== -1 ? ocrJobId.slice(idx + 1) : ocrJobId;
};

interface ActiveSubscription {
  channel: SibillSocketChannel;
  unsubscribe: () => void;
}

/**
 * Global listener for OCR "ready" events across all pending F24 uploads.
 *
 * Mounted once at the dashboard layout level.
 *
 * For each upload in `WAITING_OCR`, we join its per-upload topic
 * `ocr:{correlation_id}` and register the `F24_OCR_READY` handler. When the
 * upload transitions out of `WAITING_OCR` (or the list changes), we leave the
 * channel. On event: invalidate the OCR caches + show a success toast with a
 * CTA pointing to `/f24/pay?f24UploadId=<id>`.
 */
export const useF24OcrReadyNotification = (): void => {
  const { t } = useTranslation('notifications');
  const companyId = useActiveCompanyId();
  const { joinChannel } = useSibillSocket();
  const { data: notificationsToken } = useNotificationsToken();
  const socketReady = Boolean(notificationsToken);

  // Only poll the OCR list when the socket is connected — otherwise there's
  // nothing to subscribe to.
  const { data: uploads } = useF24OcrListQuery(socketReady);

  // Keep track of the channels we've subscribed to, keyed by ocr_job_id, so we
  // can diff the incoming list and leave channels for uploads that are no
  // longer pending.
  const subscriptionsRef = useRef<Map<string, ActiveSubscription>>(new Map());

  // Keep stable refs for values used inside the channel handler so we don't
  // need to tear down and rebuild the subscriptions on every render.
  const tRef = useRef(t);
  tRef.current = t;
  const companyIdRef = useRef(companyId);
  companyIdRef.current = companyId;

  useEffect(() => {
    if (!socketReady || !companyId) return;

    const pendingIds = new Set(
      (uploads ?? []).filter((upload) => upload.status === 'WAITING_OCR').map((upload) => upload.ocrJobId),
    );

    // Leave channels for uploads that are no longer in WAITING_OCR
    for (const [ocrJobId, subscription] of subscriptionsRef.current.entries()) {
      if (!pendingIds.has(ocrJobId)) {
        subscription.unsubscribe();
        subscription.channel.leave();
        subscriptionsRef.current.delete(ocrJobId);
      }
    }

    // Join channels for newly pending uploads
    for (const ocrJobId of pendingIds) {
      if (subscriptionsRef.current.has(ocrJobId)) continue;

      const correlationId = getCorrelationId(ocrJobId);
      const channel = joinChannel(`ocr:${correlationId}`, {
        company_id: companyId,
        ocr_job_id: ocrJobId,
      });

      if (!channel) continue;

      const unsubscribe = channel.registerEvent<F24OcrReadyPayload>('F24_OCR_READY', (payload) => {
        if (payload.ocr_job_id !== ocrJobId) return;

        const currentCompanyId = companyIdRef.current;
        if (currentCompanyId) {
          queryClient.invalidateQueries({ queryKey: F24QueryKeys.ocrList(currentCompanyId) });
          queryClient.invalidateQueries({ queryKey: F24QueryKeys.ocrItem(currentCompanyId, payload.ocr_job_id) });
        }

        snack.enqueue(
          {
            message: tRef.current('f24_ocr_ready_notification.message'),
            type: 'success',
            link: {
              href: `${RouteConfig.F24Pay.buildLink()}?f24UploadId=${payload.ocr_job_id}`,
              label: tRef.current('f24_ocr_ready_notification.link_text'),
            },
          },
          { timeout: 10000 },
        );
      });

      subscriptionsRef.current.set(ocrJobId, { channel, unsubscribe });
    }
  }, [uploads, socketReady, companyId, joinChannel]);

  // Clean up every subscription when the hook unmounts (logout, tab close, …)
  useEffect(() => {
    const subscriptions = subscriptionsRef.current;
    return () => {
      for (const subscription of subscriptions.values()) {
        subscription.unsubscribe();
        subscription.channel.leave();
      }
      subscriptions.clear();
    };
  }, []);
};
